import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCountUp } from '../../hooks/useCountUp';
import { conflictsApi } from '../../api/conflictsApi';
import { useLocationOptions } from '../Admin/shared/useLocationOptions';
import { useCollectionSync } from '../../hooks/useCollectionSync';
import { toast } from 'react-toastify';

function StateDropdown({ value, onChange, options, loading }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none pl-3 pr-8 py-1.5 text-[12px] font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-green-400 hover:border-gray-300 cursor-pointer"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {loading && option.value === 'National' ? 'Loading states...' : option.label}
          </option>
        ))}
      </select>
      <svg
        className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

function StatCard({ end, isPercent = false, suffix, label, accent, accentColor = 'green' }) {
  const { count, ref } = useCountUp(end, 1800);
  const displayed = isPercent ? `${count}%` : count.toLocaleString('en-NG');

  return (
    <div ref={ref} className="bg-gray-50 rounded-xl p-5 flex-1 min-w-0">
      <p className="text-[26px] font-extrabold text-gray-900 leading-tight tabular-nums">
        {displayed}
        {suffix && <span className="text-[26px] font-extrabold text-gray-900"> {suffix}</span>}
      </p>
      <p className="text-[12px] text-gray-400 mt-1 leading-snug">{label}</p>
      <p className={`text-[11px] font-medium mt-1 leading-snug ${accentColor === 'green' ? 'text-green-500' : 'text-gray-400'}`}>
        {accent}
      </p>
    </div>
  );
}

function parseDateLike(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const text = String(value).trim();
  if (!text) return null;

  const direct = new Date(text);
  if (!Number.isNaN(direct.getTime())) return direct;

  const match = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (!match) return null;

  const [, dd, mm, yyyy] = match;
  const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isRecentMeeting(value) {
  const date = parseDateLike(value);
  if (!date) return false;
  const sixtyDaysAgo = Date.now() - 60 * 24 * 60 * 60 * 1000;
  return date.getTime() >= sixtyDaysAgo;
}

function isActiveCommittee(row = {}) {
  const status = String(row.status ?? row.statusLabel ?? row.data_status ?? row.dataStatus ?? '').trim().toLowerCase();
  if (!status) return false;
  return !['inactive', 'closed', 'suspended', 'rejected'].includes(status);
}

function normalizeStateOptions(stateOptions = []) {
  return [
    { value: 'National', label: 'National' },
    ...stateOptions,
  ];
}

export default function ConflictSection() {
  const [state, setState] = useState('National');
  const [records, setRecords] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { stateOptions, loadingStates } = useLocationOptions();

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [recordResult, committeeResult] = await Promise.all([
        conflictsApi.records.listAll({ per_page: 1000, page: 1 }),
        conflictsApi.committees.listAll({ per_page: 1000, page: 1 }),
      ]);

      setRecords(Array.isArray(recordResult?.items) ? recordResult.items : []);
      setCommittees(Array.isArray(committeeResult?.items) ? committeeResult.items : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to load conflict summary.';
      setError(msg);
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useCollectionSync([conflictsApi.records.collectionKey, conflictsApi.committees.collectionKey], loadSummary);

  const stateChoices = useMemo(() => normalizeStateOptions(stateOptions), [stateOptions]);

  const filteredRecords = useMemo(() => {
    if (state === 'National') return records;
    return records.filter((row) => String(row.state ?? '').trim() === state);
  }, [records, state]);

  const filteredCommittees = useMemo(() => {
    if (state === 'National') return committees;
    return committees.filter((row) => String(row.state ?? '').trim() === state);
  }, [committees, state]);

  const totalCases = filteredRecords.length;
  const totalCommittees = filteredCommittees.length;
  const activeCommittees = filteredCommittees.filter(isActiveCommittee).length;
  const recentMeetingCommittees = filteredCommittees.filter((row) => isRecentMeeting(row.last_meeting_date ?? row.lastMeetingDate)).length;
  const activePct = totalCommittees > 0 ? Math.round((activeCommittees / totalCommittees) * 100) : 0;
  const recentPct = totalCommittees > 0 ? Math.round((recentMeetingCommittees / totalCommittees) * 100) : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-[17px] font-bold text-gray-900">
            Conflict &amp; Dispute Records Summary
          </h2>
          <p className="text-[12px] text-gray-400 mt-0.5">
            Live conflict incidents and committee activity across states
          </p>
        </div>
        <StateDropdown
          value={state}
          onChange={setState}
          options={stateChoices}
          loading={loadingStates}
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="flex gap-4">
        <StatCard
          key={`${state}-cases`}
          end={totalCases}
          suffix="Cases"
          label="Total Conflict Incidents"
          accent={loading ? 'Loading live records...' : 'Live conflict record count'}
          accentColor="green"
        />
        <StatCard
          key={`${state}-committees`}
          end={totalCommittees}
          suffix="Committees"
          label="Committee Registry"
          accent={loading ? 'Loading live records...' : 'Live committee registry'}
          accentColor="green"
        />
        <StatCard
          key={`${state}-active`}
          end={activePct}
          isPercent
          suffix="Active"
          label="Active Committees"
          accent={`${activeCommittees} of ${totalCommittees || 0} committees active`}
          accentColor="green"
        />
        <StatCard
          key={`${state}-recent`}
          end={recentPct}
          isPercent
          suffix="Recent"
          label="Recent Meeting Coverage"
          accent={`${recentMeetingCommittees} committees met within 60 days`}
          accentColor="green"
        />
      </div>
    </div>
  );
}
